/**
 * The test sends of this page, one per automation key (TEST-EMAIL T3).
 *
 * WHY A MODULE-LEVEL STORE AND NOT COMPONENT STATE (adversarial review, two
 * rounds). The page can unmount and remount the template tab while a send is on
 * its way: a phone turned to landscape moves the workspace out of the sheet, a
 * browser Back changes or clears the automation, the sheet's exit animation
 * keeps a dialog on screen half a second after the workspace has gone. A send
 * owned by one component instance was lost with it — its lock (so the next
 * instance could send again at once) and its answer (so the reader never saw
 * "gesendet" or "unklar … nicht sofort erneut senden"). Here the send belongs to
 * the automation key for the page's lifetime, and every instance reads the same
 * entry through `useSyncExternalStore`, so there is no window between a render
 * and an effect in which an instance can see a stale state. Same shape as the
 * activity-category catalog (`activityCategoryCatalogStore`).
 *
 * WHAT AN ENTRY SAYS. `status` is `idle`, `pending`, `sent` or `failed`;
 * `owner` is the instance that started it (only that one keeps its dialog shut
 * while it runs); `seen` says whether the reader has seen the answer.
 *
 * AN ANSWER IS SEEN EXACTLY ONCE. It is shown in a dialog that is open for its
 * automation when it arrives, and counts as seen when the reader closes that
 * dialog or follows its link. If no dialog is open when it arrives, or the
 * dialog showing it disappears without the reader closing it, it is said
 * through the `report` the sender gave (a toast) — once. A later opening
 * starts fresh only after that.
 *
 * SCOPED TO THE SIGNED-IN SESSION (adversarial review). The store lives as
 * long as the single-page app, and logging out, logging in as someone else or
 * switching the support-mode organisation does not reload it. An entry carries
 * the session it was started in — the token's user and, in support mode, its
 * organisation — and is invisible to any other: another session reads it as
 * idle, can send its own, and an answer that arrives after the session changed
 * is dropped, never shown or toasted to whoever is signed in now (it names the
 * other organisation's test address). The server keeps the record either way.
 *
 * NOTHING HERE TALKS TO THE SERVER OR WRITES GERMAN. The request is the
 * caller's `run`, the words are the caller's `report`.
 */
import { getTokenPayload } from "../../../authStore";

const IDLE = Object.freeze({
  status: "idle",
  result: null,
  error: null,
  owner: null,
  seen: true,
});

const sends = new Map();
const openDialogs = new Map();
const reporters = new Map();
const listeners = new Set();

/** Who is signed in, and for which organisation in support mode. */
function sessionScope() {
  const payload = getTokenPayload();
  return `${payload?.sub ?? ""}|${payload?.imp_org ?? ""}`;
}

function emit() {
  for (const listener of Array.from(listeners)) listener();
}

function put(key, entry) {
  sends.set(key, entry);
  emit();
}

const settled = (entry) => entry.status === "sent" || entry.status === "failed";

function reportOnce(key, entry) {
  const report = reporters.get(key);
  put(key, { ...entry, seen: true });
  if (report) report(entry);
}

export function getTestEmailSend(key) {
  const entry = sends.get(key);
  return entry && entry.scope === sessionScope() ? entry : IDLE;
}

export function subscribeTestEmailSends(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Send once for this automation. Resolves to `{ ok, result | error }`, or to
 * `null` when a send for it is already on its way anywhere on the page.
 */
export async function startTestEmailSend(key, { owner, run, report }) {
  if (getTestEmailSend(key).status === "pending") return null;
  const scope = sessionScope();
  const started = { status: "pending", result: null, error: null, owner, seen: false, scope };
  reporters.set(key, report);
  put(key, started);
  let outcome;
  try {
    outcome = { ok: true, result: await run() };
  } catch (error) {
    outcome = { ok: false, error };
  }
  // Not ours any more (a reset, or another session's send took the key), or
  // the session changed meanwhile: the answer is not for whoever is here now.
  if (sends.get(key) !== started) return outcome;
  if (scope !== sessionScope()) {
    sends.delete(key);
    emit();
    return outcome;
  }
  const entry = outcome.ok
    ? { status: "sent", result: outcome.result, error: null, owner, seen: false, scope }
    : { status: "failed", result: null, error: outcome.error, owner, seen: false, scope };
  if ((openDialogs.get(key) || 0) > 0) put(key, entry);
  else reportOnce(key, entry);
  return outcome;
}

/** A dialog for this automation is open and will show what arrives. */
export function testEmailDialogOpened(key) {
  openDialogs.set(key, (openDialogs.get(key) || 0) + 1);
}

/**
 * A dialog for this automation went away. `byReader`: the reader closed it,
 * having seen what it showed. Otherwise it vanished (an unmount) and an
 * answer it was showing is reported, unless another dialog still shows it.
 */
export function testEmailDialogClosed(key, { byReader }) {
  const left = Math.max(0, (openDialogs.get(key) || 0) - 1);
  openDialogs.set(key, left);
  const entry = getTestEmailSend(key);
  if (!settled(entry) || entry.seen) return;
  if (byReader) put(key, { ...entry, seen: true });
  else if (left === 0) reportOnce(key, entry);
}

/** The reader has seen the answer some other way (followed its link). */
export function acknowledgeTestEmailSend(key) {
  const entry = getTestEmailSend(key);
  if (settled(entry) && !entry.seen) put(key, { ...entry, seen: true });
}

/**
 * A new opening starts fresh — except over a send still on its way (the dialog
 * shows it running) or an answer nobody has seen yet (the dialog shows it).
 */
export function beginTestEmailOpening(key) {
  const entry = getTestEmailSend(key);
  if (entry.status === "pending" || (settled(entry) && !entry.seen)) return;
  if (entry.status !== "idle") put(key, IDLE);
}

/**
 * A refusal the reader answers in the open form (choosing another scenario) is
 * taken back; nothing else is.
 */
export function clearTestEmailRefusal(key) {
  if (getTestEmailSend(key).status === "failed") put(key, IDLE);
}

/** Test-only: the module-level state would otherwise leak across tests. */
export function __resetAutomationTestEmailSends() {
  sends.clear();
  openDialogs.clear();
  reporters.clear();
  emit();
}
